import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { COMPANY } from "../data/company";
import { OG_IMAGE, PAGES, SITE, absolute, jsonLd } from "../data/seo";
import { alternatesOf, byPath } from "../data/registry";

const NOT_FOUND_TITLE = `Page not found | ${COMPANY.product}`;

const HTML_LANG = { "en-IN": "en", "hi-IN": "hi" };
const OG_LOCALE = { "en-IN": "en_IN", "hi-IN": "hi_IN" };

/** hreflang links for a page and its twin; the English page is x-default. */
function hreflangs(entry) {
  const twins = alternatesOf(entry);
  if (twins.length === 0) return [];
  const pages = [entry, ...twins];
  const english = pages.find((p) => p.lang === "en-IN") ?? entry;
  return [
    ...pages.map((p) => ({ hreflang: p.lang, href: absolute(p.path) })),
    { hreflang: "x-default", href: absolute(english.path) },
  ];
}

/**
 * The current route's head tags.
 *
 * Rendered once, in App, and keyed off the location rather than dropped into
 * each page: a page cannot forget it, and seo.test.js can check the whole map
 * against App.js in one place.
 *
 * React 19 hoists <title>, <meta> and <link> into <head> wherever they are
 * rendered, and removes them again when this unmounts, so client-side
 * navigation swaps them cleanly. The JSON-LD <script> is NOT hoisted -- only
 * async scripts are -- and stays in the body, which Google reads just as well.
 *
 * `data-seo` marks every tag this renders. scripts/prerender.mjs saves them
 * into the static HTML, and index.js removes the saved copies before React
 * mounts so the live ones are not added alongside them.
 *
 * An unknown path gets the 404 tags: a noindex robots meta and no canonical,
 * matching what NotFound renders and what prerender saves as 404.html.
 */
export default function Seo() {
  const { pathname } = useLocation();
  const known = Boolean(PAGES[pathname]);
  const entry = known ? byPath(pathname) : undefined;
  const lang = entry?.lang ?? "en-IN";

  useEffect(() => {
    document.documentElement.lang = HTML_LANG[lang] ?? "en";
  }, [lang]);

  if (!known) {
    return (
      <>
        <title data-seo="">{NOT_FOUND_TITLE}</title>
        <meta data-seo="" name="robots" content="noindex" />
      </>
    );
  }

  const path = pathname;
  const { title, description } = PAGES[path];
  const url = absolute(path);
  const image = entry?.chunk ? `${SITE}/og/${entry.chunk}.png` : OG_IMAGE;

  // Escaped so a "</script>" inside any copy cannot close the tag early.
  const ld = JSON.stringify(jsonLd(path)).replace(/</g, "\\u003c");

  return (
    <>
      <title data-seo="">{title}</title>
      <meta data-seo="" name="description" content={description} />
      <link data-seo="" rel="canonical" href={url} />
      {hreflangs(entry).map(({ hreflang, href }) => (
        <link data-seo="" key={hreflang} rel="alternate" hrefLang={hreflang} href={href} />
      ))}

      <meta data-seo="" property="og:type" content="website" />
      <meta data-seo="" property="og:site_name" content={COMPANY.product} />
      <meta data-seo="" property="og:locale" content={OG_LOCALE[lang] ?? "en_IN"} />
      <meta data-seo="" property="og:url" content={url} />
      <meta data-seo="" property="og:title" content={title} />
      <meta data-seo="" property="og:description" content={description} />
      <meta data-seo="" property="og:image" content={image} />
      <meta data-seo="" property="og:image:width" content="1200" />
      <meta data-seo="" property="og:image:height" content="630" />
      <meta data-seo="" name="twitter:card" content="summary_large_image" />
      <meta data-seo="" name="twitter:title" content={title} />
      <meta data-seo="" name="twitter:description" content={description} />
      <meta data-seo="" name="twitter:image" content={image} />

      <script
        data-seo=""
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: ld }}
      />
    </>
  );
}
